import { Link } from "react-router-dom";
import { ArrowRight, Gift } from "lucide-react";
import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { useSeason, GIFT_SETS } from "@/lib/holiday";
import { useProducts, money, cleanTitle } from "@/lib/api";
import { useCart, toLine } from "@/lib/cart";
import { SplitText, Magnetic, Reveal } from "@/components/fx/Motion";
import { Countdown } from "./Countdown";

export const HolidayBanner = () => {
  const { t } = useI18n();
  const { active, season, image, maxPct } = useSeason();
  if (!active) return null;
  return (
    <section data-testid="holiday-banner" className="relative min-h-[70vh] flex items-center overflow-hidden">
      <motion.img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" initial={{ scale: 1.2 }} animate={{ scale: 1 }} transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }} />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b0a08]/95 via-[#0b0a08]/60 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 w-full py-20">
        <p className="eyebrow mb-5 flex items-center gap-2"><Gift size={14} /> {t(season === "newyear" ? "ny_eyebrow" : "xmas_eyebrow")}</p>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.98] max-w-3xl"><SplitText text={t("holiday_title")} stagger={0.08} /></h2>
        <Reveal delay={0.4}><p className="text-white/75 max-w-md mt-6">{t("holiday_text")}</p></Reveal>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-8"><Countdown /></motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="flex flex-col sm:flex-row gap-3 mt-8">
          <Magnetic><Link data-testid="holiday-sets-cta" to="/gifts#sets" className="btn-gold btn-shine h-14 px-10 flex items-center justify-center gap-3">{t("holiday_cta_sets")} <ArrowRight size={16} /></Link></Magnetic>
          <Magnetic><Link data-testid="holiday-quiz-cta" to="/gifts#quiz" className="btn-ghost h-14 px-8 flex items-center justify-center">{t("quiz_start")}</Link></Magnetic>
          <Magnetic><Link data-testid="holiday-box-cta" to="/gifts#box" className="btn-ghost h-14 px-10 flex items-center justify-center">{t("holiday_cta_box")}</Link></Magnetic>
        </motion.div>
        {maxPct > 0 && (
          <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: -8 }} transition={{ delay: 1.2, type: "spring", stiffness: 140 }}
            className="hidden md:flex absolute right-10 top-16 h-36 w-36 rounded-full bg-[#c8102e] text-white text-center items-center justify-center font-display text-2xl leading-tight shadow-2xl border-4 border-[#e3cb94]">
            -{maxPct}%
          </motion.div>
        )}
      </div>
    </section>
  );
};

const SetCard = ({ set, products, tier, i }) => {
  const { t } = useI18n();
  const { add, checkout } = useCart();
  const items = set.handles.map((h) => products.find((p) => p.handle === h)).filter(Boolean);
  if (items.length !== set.size || !tier) return null;
  const lines = items.map((p) => toLine(p, p.variants[0]));
  const total = items.reduce((s, p) => s + p.price, 0);
  const final = total * (1 - tier.pct / 100);
  const addAll = () => { lines.forEach((l, idx) => add(l, idx === lines.length - 1, idx === lines.length - 1)); };

  return (
    <motion.div data-testid={`gift-set-${set.size}`} initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: i * 0.15 }}
      whileHover={{ y: -8 }} className="relative border border-[#c9a55c]/30 bg-[#0e0d0b] p-6 flex flex-col overflow-hidden">
      <span className="absolute top-4 right-4 bg-[#c8102e] text-white text-[11px] font-bold px-2.5 py-1">-{tier.pct}%</span>
      <p className="eyebrow">{t("set_n", { n: set.size })}</p>
      <p className="font-display text-3xl mt-2">{t(`set_name_${set.size}`)}</p>
      <div className={`grid gap-2 my-6 ${set.size === 5 ? "grid-cols-5" : set.size === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
        {items.map((p) => <Link key={p.id} to={`/products/${p.handle}`} className="aspect-[3/4] bg-white"><img src={p.images[0]} alt={p.title} className="h-full w-full object-contain p-1" /></Link>)}
      </div>
      <ul className="text-xs text-white/60 space-y-1 flex-1">{items.map((p) => <li key={p.id}>· {cleanTitle(p.title)}</li>)}</ul>
      <div className="flex items-baseline gap-3 mt-5">
        <span data-testid={`gift-set-price-${set.size}`} className="text-2xl font-semibold">{money(final)}</span>
        <span className="line-through text-white/40 text-sm">{money(total)}</span>
      </div>
      <button data-testid={`gift-set-buy-${set.size}`} onClick={() => checkout(lines, tier.code)} className="btn-gold btn-shine h-12 mt-4">{t("set_buy")}</button>
      <button data-testid={`gift-set-add-${set.size}`} onClick={addAll} className="btn-ghost h-11 mt-2">{t("set_add")}</button>
    </motion.div>
  );
};

export const GiftSets = () => {
  const { t } = useI18n();
  const { tierFor } = useSeason();
  const { data = [] } = useProducts({});
  return (
    <section id="sets" data-testid="gift-sets-section" className="max-w-7xl mx-auto px-5 lg:px-10 py-24 scroll-mt-28">
      <p className="eyebrow mb-4">{t("sets_eyebrow")}</p>
      <h2 className="font-display text-3xl lg:text-5xl mb-12"><SplitText text={t("sets_title")} /></h2>
      <div className="grid md:grid-cols-3 gap-6">
        {GIFT_SETS.map((s, i) => <SetCard key={s.size} set={s} products={data} tier={tierFor(s.size)} i={i} />)}
      </div>
    </section>
  );
};
